import { useState } from 'react';
import { createEmployeeSchema, type Employee } from '@faktorplus/shared';
import { useCreateEmployee, useEmployees, useUpdateEmployee } from '../../api/dictionaries';
import { Button } from '../../components/ui/button';
import { PencilIcon } from '../../components/ui/icons';
import { Field, Input } from '../../components/ui/form';
import { Modal, modalStyles } from '../../components/ui/modal';
import { EmptyState, ErrorBox, LoadingBlock } from '../../components/ui/feedback';
import { ApiError } from '../../lib/api-client';
import pageStyles from '../../components/layout/page.module.css';
import styles from './settings-page.module.css';

/**
 * The people list, typed in by hand — there is no export from payroll to read
 * (decision of 3.09.2026). Small enough that it never needs paging.
 */
export function EmployeesTab() {
  // Inactive people stay listed, or switching one off would be irreversible:
  // this is the only screen that can switch them back on.
  const { data: employees = [], isLoading, error } = useEmployees(true);
  const [editing, setEditing] = useState<Employee | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorBox>Nie udało się pobrać listy pracowników.</ErrorBox>;

  return (
    <>
      <div className={pageStyles.header}>
        <span className={pageStyles.subtitle}>
          Osoby, do których można przypisać fakturę — odzież robocza, paliwo, narzędzie
          kupione dla konkretnego pracownika
        </span>
        <div className={pageStyles.actions}>
          <Button variant="primary" onClick={() => setIsCreating(true)}>
            + Nowy pracownik
          </Button>
        </div>
      </div>

      {employees.length === 0 ? (
        <EmptyState
          title="Brak pracowników"
          description="Dodaj pierwszą osobę, żeby móc przypisywać do niej faktury."
        />
      ) : (
        <div className={pageStyles.tableWrap}>
          <table className={pageStyles.table} style={{ minWidth: 480 }}>
            <thead>
              <tr>
                <th>Imię i nazwisko</th>
                <th>Stan</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {employees.map((employee) => (
                <tr key={employee.id} className={employee.isActive ? undefined : styles.inactive}>
                  <td className={pageStyles.strong}>{employee.fullName}</td>
                  <td className={pageStyles.muted}>
                    {employee.isActive ? 'Aktywny' : 'Nie pracuje'}
                  </td>
                  <td>
                    <div className={styles.rowActions}>
                      <Button variant="soft" icon={<PencilIcon />} onClick={() => setEditing(employee)}>
                        Edytuj
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(isCreating || editing) && (
        <EmployeeDialog
          employee={editing}
          onClose={() => {
            setIsCreating(false);
            setEditing(null);
          }}
        />
      )}
    </>
  );
}

function EmployeeDialog({
  employee,
  onClose,
}: {
  employee: Employee | null;
  onClose: () => void;
}) {
  const create = useCreateEmployee();
  const update = useUpdateEmployee();

  const [fullName, setFullName] = useState(employee?.fullName ?? '');
  const [isActive, setIsActive] = useState(employee?.isActive ?? true);
  const [validationError, setValidationError] = useState<string | null>(null);

  const isPending = create.isPending || update.isPending;
  const serverError = create.error ?? update.error;

  async function handleSubmit() {
    setValidationError(null);

    const parsed = createEmployeeSchema.safeParse({ fullName });

    if (!parsed.success) {
      setValidationError(parsed.error.issues[0]?.message ?? 'Sprawdź wprowadzone dane');
      return;
    }

    if (employee) {
      await update.mutateAsync({ id: employee.id, input: { ...parsed.data, isActive } });
    } else {
      await create.mutateAsync(parsed.data);
    }
    onClose();
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={employee ? 'Edytuj pracownika' : 'Nowy pracownik'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            Anuluj
          </Button>
          <span className={modalStyles.footerSpacer} />
          <Button variant="primary" isLoading={isPending} onClick={() => void handleSubmit()}>
            {employee ? 'Zapisz zmiany' : 'Dodaj pracownika'}
          </Button>
        </>
      }
    >
      <Field label="Imię i nazwisko">
        {(id) => (
          <Input
            id={id}
            value={fullName}
            autoFocus={!employee}
            placeholder="Jan Kowalski"
            onChange={(event) => setFullName(event.target.value)}
          />
        )}
      </Field>

      {employee && (
        <label className={modalStyles.checkItem}>
          <input
            type="checkbox"
            className={pageStyles.checkbox}
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
          />
          Nadal pracuje
        </label>
      )}

      {validationError && <ErrorBox>{validationError}</ErrorBox>}
      {serverError && (
        <ErrorBox>
          {serverError instanceof ApiError ? serverError.message : 'Nie udało się zapisać.'}
        </ErrorBox>
      )}
    </Modal>
  );
}
